<script lang="ts">
  import type { ChatMessageAdmin } from '@pin/schemas/socket';
  import { banUser, muteUser, unBanUser, unMuteUser } from '../api/api';
  import { adminName } from '../stores/configStore';
  import { addToast } from '../stores/toastStore';
  import { parseDurationToSeconds } from '../utils/regex';

  interface Props {
    user: ChatMessageAdmin['user'];
    isBanned?: boolean;
    /** Null when it is not known: both mute and unmute are offered. */
    isMuted?: boolean | null;
    disabled?: boolean;
    /** Set while an action is running. */
    busy?: boolean;
    /** `down` where there is no room above, as at the top of a pane. */
    placement?: 'up' | 'down';
    /**
     * Fills in the account a child profile acts through, for a user that
     * came without it. Null when that is not known: the action goes to the
     * user as given, with a warning.
     */
    resolveUser?: (
      user: ChatMessageAdmin['user']
    ) => Promise<ChatMessageAdmin['user'] | null>;
    onModerationActive?: (active: boolean) => void;
  }

  let {
    user,
    isBanned,
    isMuted,
    disabled = false,
    // eslint-disable-next-line no-useless-assignment -- This is a false positive
    busy = $bindable(false),
    placement = 'up',
    resolveUser,
    onModerationActive,
  }: Props = $props();

  const presets = ['30s', '5m', '60m'];

  let showTimeoutOptions = $state(false);
  let showBanUserOptions = $state(false);
  let showUnbanuserOptions = $state(false);
  let showMuteOptions = $state(false);
  let showUnmuteOptions = $state(false);
  let actionButtonHovered = $state(false);

  let timeoutValue = $state('');
  let muteValue = $state('');
  let reason = $state('');

  let timeoutInProgress = $state(false);
  let userBanInProgress = $state(false);
  let userUnbanInprogress = $state(false);
  let userMuteInProgress = $state(false);
  let userUnmuteInProgress = $state(false);
  let inProgress = $derived(
    timeoutInProgress ||
      userBanInProgress ||
      userUnbanInprogress ||
      userMuteInProgress ||
      userUnmuteInProgress
  );
  $effect(() => {
    busy = inProgress;
  });

  const reset = () => {
    showTimeoutOptions = false;
    showBanUserOptions = false;
    showUnbanuserOptions = false;
    showMuteOptions = false;
    showUnmuteOptions = false;
    timeoutValue = '';
    muteValue = '';
    reason = '';
  };

  // The user is taken when the action starts, so a toast names the user
  // acted on even if `user` changes while the request runs
  const accountOf = async (target: ChatMessageAdmin['user']) => {
    const resolved = resolveUser == null ? target : await resolveUser(target);
    return {
      userId: (resolved ?? target).id,
      viaUserId: (resolved ?? target).viaUserId,
      accountUnknown: resolved == null,
    };
  };

  // client-api bans and mutes the account a child profile acts through
  const warnIfAccountUnknown = (
    accountUnknown: boolean,
    target: ChatMessageAdmin['user'],
    done: string
  ) => {
    if (accountUnknown) {
      addToast({
        message: `No message of '${target.name}' was found: if this is a child profile, the account it acts through was not ${done}`,
        type: 'error',
        timeout: 10_000,
      });
    }
  };

  const onTimeout = async (value: string) => {
    const target = user;
    const timeoutReason = reason;
    const result = parseDurationToSeconds(value);
    if (!result.ok) {
      return addToast({ message: result.error, type: 'error' });
    }
    const durationS = result.duration;

    showTimeoutOptions = false;
    timeoutInProgress = true;
    try {
      const { accountUnknown, ...account } = await accountOf(target);
      await banUser({
        ...account,
        issuedBy: $adminName,
        reason: timeoutReason,
        durationS,
      });
      addToast({ message: `'${target.name}' was timed out (${value})` });
      warnIfAccountUnknown(accountUnknown, target, 'timed out');
    } catch (e) {
      addToast({
        message: `Couldn't timeout '${target.name}': ${String(e)}`,
        type: 'error',
      });
    } finally {
      reset();
      timeoutInProgress = false;
    }
  };

  const onBanUser = async () => {
    const target = user;
    const banReason = reason;
    showBanUserOptions = false;
    userBanInProgress = true;
    try {
      const { accountUnknown, ...account } = await accountOf(target);
      await banUser({
        ...account,
        issuedBy: $adminName,
        reason: banReason,
        durationS: null,
      });
      addToast({ message: `'${target.name}' was banned` });
      warnIfAccountUnknown(accountUnknown, target, 'banned');
    } catch (e) {
      addToast({
        message: `Couldn't ban '${target.name}': ${String(e)}`,
        type: 'error',
      });
    } finally {
      reset();
      userBanInProgress = false;
    }
  };

  const onUnBanUser = async () => {
    const target = user;
    showUnbanuserOptions = false;
    userUnbanInprogress = true;
    try {
      const { accountUnknown, ...account } = await accountOf(target);
      await unBanUser({
        ...account,
        issuedBy: $adminName,
      });
      addToast({ message: `'${target.name}' was unbanned` });
      warnIfAccountUnknown(accountUnknown, target, 'unbanned');
    } catch (e) {
      addToast({
        message: `Couldn't unban '${target.name}': ${String(e)}`,
        type: 'error',
      });
    } finally {
      reset();
      userUnbanInprogress = false;
    }
  };

  const onMuteUser = async (value: string) => {
    const target = user;
    const result = parseDurationToSeconds(value);
    if (!result.ok) {
      return addToast({ message: result.error, type: 'error' });
    }
    showMuteOptions = false;
    userMuteInProgress = true;
    try {
      const { accountUnknown, ...account } = await accountOf(target);
      await muteUser({
        ...account,
        issuedBy: $adminName,
        durationS: result.duration,
      });
      addToast({ message: `'${target.name}' was muted (${value})` });
      warnIfAccountUnknown(accountUnknown, target, 'muted');
    } catch (e) {
      addToast({
        message: `Couldn't mute '${target.name}': ${String(e)}`,
        type: 'error',
      });
    } finally {
      reset();
      userMuteInProgress = false;
    }
  };

  const onUnMuteUser = async () => {
    const target = user;
    showUnmuteOptions = false;
    userUnmuteInProgress = true;
    try {
      const { accountUnknown, ...account } = await accountOf(target);
      await unMuteUser({
        ...account,
        issuedBy: $adminName,
      });
      addToast({ message: `'${target.name}' was unmuted` });
      warnIfAccountUnknown(accountUnknown, target, 'unmuted');
    } catch (e) {
      addToast({
        message: `Couldn't unmute '${target.name}': ${String(e)}`,
        type: 'error',
      });
    } finally {
      reset();
      userUnmuteInProgress = false;
    }
  };

  const handleEsc = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      reset();
    }
  };

  $effect(() => {
    if (
      showTimeoutOptions ||
      showBanUserOptions ||
      showUnbanuserOptions ||
      showMuteOptions ||
      showUnmuteOptions
    ) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  });

  $effect(() => {
    const active =
      actionButtonHovered ||
      showTimeoutOptions ||
      showBanUserOptions ||
      showUnbanuserOptions ||
      showMuteOptions ||
      showUnmuteOptions;
    if (active) {
      onModerationActive?.(true);
      return () => onModerationActive?.(false);
    }
  });
</script>

<div class="user-action-buttons" class:down={placement === 'down'}>
  <div class="dropup-wrapper">
    <button
      class="btn"
      title="Timeout"
      disabled={inProgress || disabled}
      aria-busy={inProgress}
      onclick={() => {
        reset();
        showTimeoutOptions = !showTimeoutOptions;
      }}
      onmouseenter={() => (actionButtonHovered = true)}
      onmouseleave={() => (actionButtonHovered = false)}
    >
      {#if timeoutInProgress}
        <span class="spinner" aria-hidden="true"></span>
      {:else}
        ⏱
      {/if}
    </button>
    {#if showTimeoutOptions}
      <div class="dropup">
        <div class="presets">
          {#each presets as p (p)}
            <button
              class="btn preset"
              onclick={() => {
                onTimeout(p);
              }}
            >
              {p}
            </button>
          {/each}
        </div>
        <input
          type="text"
          bind:value={timeoutValue}
          placeholder="Custom (e.g. 30s)"
        />
        <input
          type="text"
          bind:value={reason}
          placeholder="Reason (optional)"
        />
        <div class="actions">
          <button class="btn cancel" onclick={reset}> Cancel </button>
          <button class="btn confirm" onclick={() => onTimeout(timeoutValue)}>
            OK
          </button>
        </div>
      </div>
    {/if}
  </div>

  {#if !isBanned}
    <div class="dropup-wrapper">
      <button
        class="btn"
        title="Ban user"
        disabled={inProgress || disabled}
        aria-busy={inProgress}
        onclick={() => {
          reset();
          showBanUserOptions = !showBanUserOptions;
        }}
        onmouseenter={() => (actionButtonHovered = true)}
        onmouseleave={() => (actionButtonHovered = false)}
      >
        {#if userBanInProgress}
          <span class="spinner" aria-hidden="true"></span>
        {:else}
          🚫
        {/if}
      </button>
      {#if showBanUserOptions}
        <div class="dropup">
          <input
            type="text"
            bind:value={reason}
            placeholder="Reason (optional)"
          />
          <div class="actions">
            <button class="btn cancel" onclick={reset}> Cancel </button>
            <button class="btn confirm" onclick={onBanUser}> OK </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if isBanned}
    <div class="dropup-wrapper">
      <button
        class="btn"
        title="Unban user"
        disabled={inProgress || disabled}
        aria-busy={inProgress}
        onclick={() => {
          reset();
          showUnbanuserOptions = !showUnbanuserOptions;
        }}
        onmouseenter={() => (actionButtonHovered = true)}
        onmouseleave={() => (actionButtonHovered = false)}
      >
        {#if userUnbanInprogress}
          <span class="spinner" aria-hidden="true"></span>
        {:else}
          ✅
        {/if}
      </button>
      {#if showUnbanuserOptions}
        <div class="dropup">
          <div class="actions">
            <button class="btn cancel" onclick={reset}> Cancel </button>
            <button class="btn confirm" onclick={onUnBanUser}> OK </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if isMuted !== true}
    <div class="dropup-wrapper">
      <button
        class="btn"
        title="Mute user"
        disabled={inProgress || disabled}
        aria-busy={inProgress}
        onclick={() => {
          reset();
          showMuteOptions = !showMuteOptions;
        }}
        onmouseenter={() => (actionButtonHovered = true)}
        onmouseleave={() => (actionButtonHovered = false)}
      >
        {#if userMuteInProgress}
          <span class="spinner" aria-hidden="true"></span>
        {:else}
          🔇
        {/if}
      </button>
      {#if showMuteOptions}
        <div class="dropup">
          <div class="presets">
            {#each presets as p (p)}
              <button class="btn preset" onclick={() => onMuteUser(p)}
                >{p}</button
              >
            {/each}
          </div>
          <input
            type="text"
            bind:value={muteValue}
            placeholder="Custom (e.g. 30s)"
          />
          <div class="actions">
            <button class="btn cancel" onclick={reset}> Cancel </button>
            <button class="btn confirm" onclick={() => onMuteUser(muteValue)}>
              OK
            </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if isMuted === true || isMuted === null}
    <div class="dropup-wrapper">
      <button
        class="btn"
        title="Unmute user"
        disabled={inProgress || disabled}
        aria-busy={inProgress}
        onclick={() => {
          reset();
          showUnmuteOptions = !showUnmuteOptions;
        }}
        onmouseenter={() => (actionButtonHovered = true)}
        onmouseleave={() => (actionButtonHovered = false)}
      >
        {#if userUnmuteInProgress}
          <span class="spinner" aria-hidden="true"></span>
        {:else}
          🔊
        {/if}
      </button>
      {#if showUnmuteOptions}
        <div class="dropup">
          <div class="actions">
            <button class="btn cancel" onclick={reset}> Cancel </button>
            <button class="btn confirm" onclick={onUnMuteUser}> OK </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .user-action-buttons {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .dropup-wrapper {
    position: relative;
  }

  .dropup {
    position: absolute;
    bottom: 110%;
    left: 0;
    background: #1e1e1e;
    border: 1px solid #333;
    border-radius: 8px;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    z-index: 10;
    min-width: 10rem;
  }
  /* From the start of the row, so a menu fits in a narrow pane. */
  .down {
    position: relative;
  }
  .down .dropup-wrapper {
    position: static;
  }
  .down .dropup {
    bottom: auto;
    top: 110%;
  }

  .btn {
    display: flex;
    background: transparent;
    border: 1px solid #444;
    border-radius: 6px;
    color: #e0e0e0;
    cursor: pointer;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    line-height: 1.2;
    transition: background 0.15s;
  }

  .btn:hover {
    background: #3a3a3a;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .presets {
    display: flex;
    justify-content: space-around;
  }

  .preset {
    font-size: 0.85rem;
    text-align: left;
  }

  .dropup input {
    width: 100%;
    padding: 0.25rem;
    border: 1px solid #555;
    border-radius: 4px;
    font-size: 0.85rem;
  }

  .actions {
    display: flex;
    gap: 0.25rem;
    margin-top: 2px;
  }

  .btn.confirm {
    background: #2563eb;
  }

  .btn.confirm:hover {
    background: #1d4ed8;
  }

  .btn.cancel {
    background: #444;
  }

  .btn.cancel:hover {
    background: #555;
  }

  .spinner {
    border: 2px solid transparent;
    border-top: 2px solid var(--color-zinc-200);
    border-radius: 50%;
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
